import { ReactNode } from "react";
import * as client from "../../../client";
import { PantheonAvatarWithoutMenu } from "../../../components/avatar/Avatar";
import { CommentContainer } from "../../../components/comments/Comment";
import { GoogleGroupIcon } from "../../../components/google-group/GoogleGroupIcon";
import { IfFeatureEnabled } from "../../../IfFeatureEnabled";
import { displayNameForUser } from "../../../util";
import { AppetiteIcon } from "../appetite/AppetiteIcon";
import { InternationalExposureFlag } from "../appetite/InternationalExposureFlag";
import { BorBadge } from "../bor/BorBadge";
import { useBorModal } from "../bor/BorModalContext";
import { SafeReportResponse } from "../Conversion";
import { colorForStatus } from "../dashboard/StatusIcon";
import { readableStatus } from "../dashboard/util";
import { formatAbbreviatedMoney } from "../Money";
import styles from "./MobileReportHeader.module.css";

type MobileReportHeaderProps = {
  safeResponse: SafeReportResponse;
  specialtyPropertyInfo: client.ReportResponse;
  reportName: string;
  assignee: client.User | undefined;
  handler: client.User | undefined;
  bor: client.BorLetterResponse | null | undefined;
  // The shared action buttons (Policy Fly / quote / kebab), built by the parent.
  actionButtons: ReactNode;
};

export const MobileReportHeader = ({
  safeResponse,
  specialtyPropertyInfo,
  reportName,
  assignee,
  handler,
  bor,
  actionButtons,
}: MobileReportHeaderProps) => {
  const { showBorModal } = useBorModal();

  const statusColor = colorForStatus(specialtyPropertyInfo.status);
  const shortStatus = readableStatus(specialtyPropertyInfo.status).split(
    " - "
  )[0];
  const brokerDisplay =
    safeResponse.broker_contact?.full_name ||
    safeResponse.broker_contact?.organization_name ||
    safeResponse.report_json.company_info.broker_name;
  const totalTiv = safeResponse.precomputed_metadata?.total_tiv;
  const effectiveDate = safeResponse.report_json.company_info.effective_date;

  return (
    <div
      className={styles.mobileHeaderCard}
      style={{ borderLeftColor: statusColor }}
    >
      <div className={styles.mobileHeaderTop}>
        <span className={styles.mobileHeaderTitle}>{reportName}</span>
        <div className={styles.mobileHeaderActions}>
          <IfFeatureEnabled feature="comments">
            <CommentContainer
              reportName={reportName}
              reportId={specialtyPropertyInfo.report_id}
              autoOpenFromUrl
            />
          </IfFeatureEnabled>
          {actionButtons}
        </div>
      </div>
      <div className={styles.mobileMetaRow}>
        <PantheonAvatarWithoutMenu
          label={displayNameForUser(assignee)}
          imageUrl={assignee?.profile_image_url}
          showName={true}
        />
        <IfFeatureEnabled feature="handler-column">
          <PantheonAvatarWithoutMenu
            label={displayNameForUser(handler)}
            imageUrl={handler?.profile_image_url}
            showName={true}
          />
        </IfFeatureEnabled>
        {specialtyPropertyInfo.google_group_email && (
          <GoogleGroupIcon
            groupName={specialtyPropertyInfo.google_group_email}
          />
        )}
        {bor && (
          <BorBadge
            bor={bor}
            reportId={specialtyPropertyInfo.report_id}
            onClick={() => showBorModal(specialtyPropertyInfo.report_id)}
          />
        )}
        <div className={styles.mobileAppetite}>
          <AppetiteIcon
            naicsInfo={specialtyPropertyInfo.report_json.naics_info}
            precomputedMetadata={specialtyPropertyInfo.precomputed_metadata}
            googleGroupEmail={specialtyPropertyInfo.google_group_email ?? null}
            createdAtTs={specialtyPropertyInfo.created_at_ts}
            status={specialtyPropertyInfo.status}
          />
        </div>
      </div>
      <div className={styles.mobileFooterRow}>
        <span className={styles.mobileStatus} style={{ color: statusColor }}>
          {shortStatus}
        </span>
        {totalTiv != null && (
          <>
            <span className={styles.sep}>·</span>
            <span className={styles.mobileTiv}>
              {formatAbbreviatedMoney(totalTiv)}
            </span>
          </>
        )}
        {effectiveDate && (
          <>
            <span className={styles.sep}>·</span>
            <span>Eff {effectiveDate}</span>
          </>
        )}
        {brokerDisplay && (
          <>
            <span className={styles.sep}>·</span>
            <span className={styles.mobileBroker}>{brokerDisplay}</span>
          </>
        )}
      </div>
      <InternationalExposureFlag
        precomputedMetadata={specialtyPropertyInfo.precomputed_metadata}
      />
    </div>
  );
};
